/* eslint-disable max-lines -- cohesive wizard state hook, splitting would obscure flow */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { WizardStep } from '@components/back-office/shared/wizard-stepper';
import { useStartConnection } from '@utils/api/connections/use-start-connection.tsx';
import { useReconnectConnection } from '@utils/api/connections/use-reconnect-connection.tsx';
import { useFinalizeConnection } from '@utils/api/connections/use-finalize-connection.tsx';
import { useTestConnection } from '@utils/api/connections/use-test-connection.tsx';
import { launchOAuthPopup } from '@utils/oauth/popup.ts';
import { getPlatformSpec } from '@utils/types/connections/platforms.ts';
import type {
  Connection,
  ConnectionDefaults,
  PlatformId
} from '@utils/types/connections/connection.ts';
import {
  INITIAL_STATE,
  type AuthErrorKind,
  type WizardState,
  type WizardStepId
} from './types';

interface UseConnectWizardArgs {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-seeded platform/attempt for the "reconnect" entry (keeps defaults). */
  reconnectFor?: Connection | null;
}

/**
 * Returns the wizard state and the handlers each step binds to. Keeping
 * everything in one hook so steps stay dumb render functions and the
 * wizard shell can decide which Next/Back buttons to enable per step.
 */
export function useConnectWizard({
  isOpen,
  onClose,
  reconnectFor
}: UseConnectWizardArgs) {
  const { t } = useTranslation();

  const seedState = useCallback(
    (seed: Connection | null | undefined): WizardState =>
      seed
        ? {
            ...INITIAL_STATE,
            platformId: seed.platform,
            defaults: seed.defaults ?? {}
          }
        : INITIAL_STATE,
    []
  );

  const [state, setState] = useState<WizardState>(() =>
    seedState(reconnectFor)
  );
  const [currentStep, setCurrentStep] = useState<WizardStepId>(
    reconnectFor ? 3 : 1
  );

  // The wizard shell stays mounted across openings — re-seed on every
  // false → true transition so a Reconnect click right after a close
  // lands on authorize, and a Connect lands on platform.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setState(seedState(reconnectFor));
      setCurrentStep(reconnectFor ? 3 : 1);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, reconnectFor, seedState]);

  const { mutateAsync: startConnection } = useStartConnection();
  const { mutateAsync: reconnectConnection } = useReconnectConnection();
  const { mutateAsync: finalizeConnection } = useFinalizeConnection();
  const { mutateAsync: testConnection } = useTestConnection();

  const platformSpec = useMemo(
    () => (state.platformId ? getPlatformSpec(state.platformId) : null),
    [state.platformId]
  );

  const steps: WizardStep[] = useMemo(
    () => [
      { id: 1, label: t('publishing-wizard-step-platform') },
      { id: 2, label: t('publishing-wizard-step-permissions') },
      { id: 3, label: t('publishing-wizard-step-authorize') },
      { id: 4, label: t('publishing-wizard-step-defaults') },
      { id: 5, label: t('publishing-wizard-step-test') },
      { id: 6, label: t('publishing-wizard-step-done') }
    ],
    [t]
  );

  const choosePlatform = useCallback((platformId: PlatformId) => {
    setState(prev => ({
      ...INITIAL_STATE,
      platformId,
      defaults: prev.platformId === platformId ? prev.defaults : {}
    }));
  }, []);

  const setCredentialField = useCallback((key: string, value: string) => {
    setState(prev => ({
      ...prev,
      credentials: { ...prev.credentials, [key]: value },
      authError: null
    }));
  }, []);

  const setDefaults = useCallback((defaults: ConnectionDefaults) => {
    setState(prev => ({ ...prev, defaults }));
  }, []);

  const setAuthError = useCallback((authError: AuthErrorKind | null) => {
    setState(prev => ({ ...prev, authError }));
  }, []);

  /**
   * Launch (or re-launch) the OAuth popup. Only used for `oauth` platforms.
   * When the wizard was opened for a reconnect, hit the per-connection
   * `/reconnect` endpoint so the backend preserves the existing row and
   * its defaults instead of creating a new one. Resolves to true on
   * success so the caller can advance.
   */
  const runOAuth = useCallback(async (): Promise<boolean> => {
    if (!state.platformId) return false;
    const spec = getPlatformSpec(state.platformId);
    if (!spec || spec.authType !== 'oauth') return false;

    setState(prev => ({ ...prev, isAuthorizing: true, authError: null }));

    try {
      const redirectUri = `${window.location.origin}/oauth/callback`;
      const { authorize_url, attempt_id } = reconnectFor
        ? await reconnectConnection({
            connectionId: reconnectFor.id,
            redirect_uri: redirectUri
          })
        : await startConnection({
            platform: state.platformId,
            redirect_uri: redirectUri
          });

      const result = await launchOAuthPopup({
        url: authorize_url,
        attemptId: attempt_id
      });

      if (result.ok && result.code) {
        setState(prev => ({
          ...prev,
          isAuthorizing: false,
          authorizeUrl: authorize_url,
          attemptId: attempt_id,
          oauthCode: result.code ?? null,
          authError: null
        }));
        return true;
      }

      setState(prev => ({
        ...prev,
        isAuthorizing: false,
        attemptId: attempt_id,
        authorizeUrl: authorize_url,
        authError: result.reason ?? 'unknown'
      }));
      return false;
    } catch {
      setState(prev => ({
        ...prev,
        isAuthorizing: false,
        authError: 'unknown'
      }));
      return false;
    }
  }, [reconnectConnection, reconnectFor, startConnection, state.platformId]);

  /** For credentials platforms: validate required fields are filled. */
  const validateCredentials = useCallback((): boolean => {
    const spec = platformSpec;
    if (!spec || spec.authType !== 'credentials' || !spec.credentialFields) {
      return true;
    }
    const missing = spec.credentialFields
      .filter(f => f.required && !state.credentials[f.key]?.trim())
      .map(f => t(f.labelKey));
    if (missing.length > 0) {
      setAuthError('credentials_invalid');
      toast.error(
        t('publishing-credentials-missing', { fields: missing.join(', ') })
      );
      return false;
    }
    setAuthError(null);
    return true;
  }, [platformSpec, setAuthError, state.credentials, t]);

  /**
   * Finalize the connection and run the first test. Called when the user
   * enters the test step. On a failed test the connection still exists —
   * backend marks it as `failed` and the user retries from the same step.
   */
  const finalizeAndTest = useCallback(async () => {
    if (!state.platformId) return;
    setState(prev => ({
      ...prev,
      isFinalizing: true,
      testResult: null
    }));

    try {
      const connection = await finalizeConnection({
        platform: state.platformId,
        attempt_id: state.attemptId ?? '',
        code: state.oauthCode ?? undefined,
        credentials:
          platformSpec?.authType === 'credentials'
            ? state.credentials
            : undefined,
        defaults: state.defaults,
        connection_id: reconnectFor?.id
      });

      const testResult = await testConnection(connection.id);

      setState(prev => ({
        ...prev,
        isFinalizing: false,
        finalizedConnection: connection,
        testResult
      }));
    } catch (err) {
      const detail =
        (err as { response?: { data?: { error_description?: string } } })
          ?.response?.data?.error_description ??
        t('publishing-finalize-failed');
      toast.error(detail);
      setState(prev => ({
        ...prev,
        isFinalizing: false,
        testResult: { ok: false, message: detail }
      }));
    }
  }, [
    finalizeConnection,
    platformSpec?.authType,
    reconnectFor?.id,
    state.attemptId,
    state.credentials,
    state.defaults,
    state.oauthCode,
    state.platformId,
    testConnection,
    t
  ]);

  const retryTest = useCallback(async () => {
    if (!state.finalizedConnection) {
      await finalizeAndTest();
      return;
    }
    setState(prev => ({ ...prev, isFinalizing: true }));
    try {
      const testResult = await testConnection(state.finalizedConnection.id);
      setState(prev => ({
        ...prev,
        isFinalizing: false,
        testResult
      }));
    } catch (err) {
      const detail =
        (err as { response?: { data?: { error_description?: string } } })
          ?.response?.data?.error_description ?? t('publishing-test-failed');
      setState(prev => ({
        ...prev,
        isFinalizing: false,
        testResult: { ok: false, message: detail }
      }));
    }
  }, [finalizeAndTest, state.finalizedConnection, testConnection, t]);

  const handleClose = useCallback(() => {
    setState(INITIAL_STATE);
    setCurrentStep(1);
    onClose();
  }, [onClose]);

  const handleBack = useCallback(() => {
    setCurrentStep(prev => (prev > 1 ? ((prev - 1) as WizardStepId) : prev));
  }, []);

  const handleNext = useCallback(async () => {
    switch (currentStep) {
      case 1: {
        if (!state.platformId) return;
        setCurrentStep(2);
        return;
      }
      case 2: {
        setCurrentStep(3);
        return;
      }
      case 3: {
        if (platformSpec?.authType === 'oauth') {
          if (state.oauthCode) {
            setCurrentStep(4);
            return;
          }
          const ok = await runOAuth();
          if (ok) setCurrentStep(4);
          return;
        }
        if (platformSpec?.authType === 'credentials') {
          if (!validateCredentials()) return;
          setCurrentStep(4);
          return;
        }
        return;
      }
      case 4: {
        setCurrentStep(5);
        await finalizeAndTest();
        return;
      }
      case 5: {
        if (state.testResult?.ok) {
          setCurrentStep(6);
        }
        return;
      }
      case 6: {
        handleClose();
        return;
      }
      default:
        return;
    }
  }, [
    currentStep,
    finalizeAndTest,
    handleClose,
    platformSpec?.authType,
    runOAuth,
    state.oauthCode,
    state.platformId,
    state.testResult?.ok,
    validateCredentials
  ]);

  const canGoNext = useMemo(() => {
    switch (currentStep) {
      case 1:
        return !!state.platformId;
      case 2:
        return true;
      case 3:
        if (platformSpec?.authType === 'oauth') {
          return !state.isAuthorizing;
        }
        return true;
      case 4:
        return true;
      case 5:
        return !!state.testResult?.ok;
      case 6:
        return true;
      default:
        return false;
    }
  }, [
    currentStep,
    platformSpec?.authType,
    state.isAuthorizing,
    state.platformId,
    state.testResult?.ok
  ]);

  return {
    state,
    steps,
    currentStep,
    setCurrentStep,
    platformSpec,
    choosePlatform,
    setCredentialField,
    setDefaults,
    setAuthError,
    runOAuth,
    finalizeAndTest,
    retryTest,
    handleNext,
    handleBack,
    handleClose,
    canGoNext,
    totalSteps: 6
  } as const;
}
